import { gridCells, gridTotal } from '@signa/shared';
/** 潰れた入力に返す値。**呼び出し側は寸法 0 を見て描画を諦める。** */
const EMPTY = { x: 0, y: 0, width: 0, height: 0 };
/** 使える寸法か。**メタデータ待ちの `<video>` は `videoWidth === 0`。** */
function usable(value) {
    return Number.isFinite(value) && value > 0;
}
/** 2 点の距離。 */
function distance(a, b) {
    if (a === undefined || b === undefined)
        return 0;
    return Math.hypot(b[0] - a[0], b[1] - a[1]);
}
/**
 * クアッドの辺の長さから見た寸法。
 *
 * **頂点は左上から時計回り**(`[左上, 右上, 右下, 左下]` —— F-08 / #28)。
 * 上下の辺の平均を幅、左右の辺の平均を高さとする。
 */
function edgeSize(quad) {
    const [tl, tr, br, bl] = quad;
    return {
        width: (distance(tl, tr) + distance(bl, br)) / 2,
        height: (distance(tl, bl) + distance(tr, br)) / 2,
    };
}
/**
 * クアッドの**辺の長さの比**(横 ÷ 縦)。
 *
 * **これは推定ではない。** 4 点から元の長方形の比率を復元する計算は
 * 主点と焦点距離の仮定が必要で、**クアッドが平行四辺形に近いと不定**になる ——
 * [ADR-0028](../../../docs/adr/0028-video-fit-and-panel-aspect.md) はその方式を
 * 採らないと決めた。ここで返すのは**画面に見えているとおりの比**である。
 *
 * 盤面の実比率が入力されていないあいだの既定として使う。**入力が無くても
 * 引き伸ばしは止まる**のが要点。
 *
 * 測れないときは `0`。
 */
export function apparentAspect(quad) {
    const { width, height } = edgeSize(quad);
    if (!usable(width) || !usable(height))
        return 0;
    return width / height;
}
/**
 * 盤面の矩形の寸法。ホモグラフィの**写し元**になる。
 *
 * `aspect` を渡すとその比率の長方形にする(#148 で入力できるようになる)。
 * 渡さなければ [`apparentAspect`](#apparentAspect) に落とす。
 *
 * **絶対の大きさに意味は無い**(ホモグラフィが吸収する)。それでも辺の長さを
 * 基準にするのは、**値を表示ピクセルの桁に収めて浮動小数の精度を保つ**ため。
 *
 * 測れないときは寸法 0 を返す。**呼び出し側は描画を諦める** ——
 * 潰れたクアッドは `rectToQuad` が `DegenerateQuadError` を投げる経路に任せる。
 */
export function panelSize(quad, aspect) {
    const edges = edgeSize(quad);
    if (!usable(edges.height))
        return { width: 0, height: 0 };
    const ratio = aspect != null && usable(aspect.width) && usable(aspect.height)
        ? aspect.width / aspect.height
        : apparentAspect(quad);
    if (!usable(ratio))
        return { width: 0, height: 0 };
    return { width: edges.height * ratio, height: edges.height };
}
/**
 * 盤面の中で素材が占める矩形。**縦横比を保って中央に収める**(`contain`)。
 *
 * 余る側に帯ができる。**切らないのが要点** ——
 * プレビューの目的は「素材がどう見えるか」の確認で、
 * **端が切れて見えないほうが困る**([ADR-0028](../../../docs/adr/0028-video-fit-and-panel-aspect.md))。
 *
 * どちらかの寸法が使えなければ寸法 0(呼び出し側は描画を諦める)。
 */
export function containRect(content, panel) {
    if (!usable(content.width) || !usable(content.height))
        return EMPTY;
    if (!usable(panel.width) || !usable(panel.height))
        return EMPTY;
    // **小さいほうの倍率を採る。** これで必ず盤面の内側に収まる。
    const scale = Math.min(panel.width / content.width, panel.height / content.height);
    const width = content.width * scale;
    const height = content.height * scale;
    return {
        x: (panel.width - width) / 2,
        y: (panel.height - height) / 2,
        width,
        height,
    };
}
/** 矩形からセルを作る。`corners` を正とし、`origin` / `size` はそこから導く。 */
function cellOf(index, left, top, right, bottom) {
    return {
        index,
        corners: [
            [left, top],
            [right, top],
            [right, bottom],
            [left, bottom],
        ],
        origin: [left, top],
        // **`right - left` で出す。** `left + width` を頂点に使うと誤差で境目がずれる。
        size: { width: right - left, height: bottom - top },
    };
}
/**
 * 枠の割り付け。**描画コアが GL に渡す値をここで決める。**
 *
 * **GL の呼び出しから判断を切り離すため**に置いている ——
 * [ADR-0019](../../../docs/adr/0019-dev-foundation.md) により WebGL と DOM 構築は
 * jsdom で試さないので、**判断がシェーダの呼び出しに埋まっていると検査できない。**
 *
 * `grid` を渡すと分割する(#152)。渡さなければセル 1 つ ——
 * 比率は [`apparentAspect`](#apparentAspect) に落ちる。
 *
 * 描けないときは `null`(クアッドが潰れている)。**呼び出し側は早期に戻る。**
 */
export function frameLayout(stageQuad, grid) {
    const total = grid == null ? null : gridTotal(grid);
    const panel = panelSize(stageQuad, total);
    if (!usable(panel.width) || !usable(panel.height))
        return null;
    if (grid == null || total == null) {
        return { panel, cells: [cellOf(0, 0, 0, panel.width, panel.height)] };
    }
    /*
     * 比率の単位を盤面座標に開く。**両側が同じ倍率を同じ境界値に掛ける**ので、
     * 隣り合うセルの境界は掛けたあとも厳密に一致する。
     */
    const scaleX = panel.width / total.width;
    const scaleY = panel.height / total.height;
    return {
        panel,
        cells: gridCells(grid).map((cell) => cellOf(cell.index, cell.left * scaleX, cell.top * scaleY, cell.right * scaleX, cell.bottom * scaleY)),
    };
}
/**
 * セルの中で素材が占める矩形(**盤面座標**)。
 *
 * セルの中に [`containRect`](#containRect) で収め、セルの位置ぶんだけずらす。
 * 余った領域はシェーダが黒帯にする(ADR-0028)。
 *
 * 描けないときは `null` —— 動画のメタデータが揃っていない(`videoWidth === 0`)、
 * またはセルが潰れている。
 */
export function cellContent(cell, video) {
    const inner = containRect(video, cell.size);
    if (!usable(inner.width) || !usable(inner.height))
        return null;
    const [x, y] = cell.origin;
    return { x: x + inner.x, y: y + inner.y, width: inner.width, height: inner.height };
}
//# sourceMappingURL=fit.js.map